import React, { useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Label, FormGroup, Input as StrapInput, FormFeedback } from "reactstrap";
import styled from "styled-components";
import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';

// Custom styled FormFeedback with red styling
const RedFormFeedback = styled(FormFeedback)`
  color: #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.05);
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
`;

// Custom styled container for the switch
const SwitchContainer = styled.div`
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    
    .rc-switch {
        margin-right: 8px;
    }
`;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
    label: string;
    required?: boolean;
    name: string; // Name is required for controlled component approach
    type?: string; // Allow 'textarea' as a type and 'checkbox' for switch
}

const Input: React.FC<InputProps> = ({ label, required, name, type = "text", ...rest }) => {
    const {
        control,
        formState: { errors, touchedFields, dirtyFields },
        clearErrors,
        trigger,
        setValue,
        getValues
    } = useFormContext();
    
    // Get error for this field
    const error = errors[name];
    const errorMessage = error ? (error.message as string) : "";
    
    // Check if field has been touched/modified
    const isTouched = Boolean(touchedFields[name]);
    const isDirty = Boolean(dirtyFields[name]);
    
    // Force re-validation on mount
    useEffect(() => {
        // Add a small delay to ensure the form is fully mounted
        const timeoutId = setTimeout(() => {
            trigger(name);
        }, 100);
        
        return () => clearTimeout(timeoutId);
    }, [name, trigger]);

    return (
        <FormGroup>
            <Label htmlFor={name} className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            <Controller
                name={name}
                control={control}
                render={({ field }) => {
                    // Common props for both input and textarea
                    const commonProps = {
                        id: name,
                        ...field,
                        onChange: (e) => {
                            // Handle number type conversion for number inputs
                            if (type === "number" && e.target.value) {
                                // Convert string to number for number inputs
                                field.onChange(parseFloat(e.target.value));
                            } else {
                                // For other types, just pass the value as is
                                field.onChange(e.target.value);
                            }
                            
                            // Clear error for this field if we have a value
                            if (e.target.value && error) {
                                // Delay clearing to avoid race conditions
                                setTimeout(() => {
                                    trigger(name);
                                }, 0);
                            }
                            
                            // Call any additional onChange handler from props
                            if (rest.onChange) {
                                rest.onChange(e);
                            }
                        },
                        onBlur: (e) => {
                            // Call the field onBlur
                            field.onBlur();
                            
                            // Trigger validation on blur
                            trigger(name);
                            
                            // Call any additional onBlur handler from props
                            if (rest.onBlur) {
                                rest.onBlur(e);
                            }
                        },
                        className: `mt-1 block w-full ${
                            error ? "border-danger" : "border"
                        } rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm px-3 py-2`,
                        invalid: Boolean(error),
                        // Only show valid checkmark if:
                        // 1. There's a value
                        // 2. No error
                        // 3. Field has been touched/interacted with
                        valid: Boolean(field.value) && !Boolean(error) && (isDirty || isTouched)
                    };
                    
                    // Handle different input types
                    if (type === "textarea") {
                        return (
                            <>
                                <StrapInput
                                    type="textarea"
                                    rows={4}
                                    {...commonProps}
                                    {...rest}
                                />
                                {error && (
                                    <FormFeedback>{errorMessage}</FormFeedback>
                                )}
                            </>
                        );
                    } else if (type === 'checkbox') {
                        // Make sure we're dealing with a boolean value
                        const isChecked = Boolean(field.value);
                        
                        // Use rc-switch for checkbox type
                        return (
                            <>
                                <SwitchContainer>
                                    <Switch
                                        checked={isChecked}
                                        onChange={(checked) => {
                                            // IMPORTANT: Use setValue directly to ensure the value updates in the form
                                            setValue(name, checked, { 
                                                shouldValidate: true,  // Validate after change
                                                shouldDirty: true      // Mark as dirty
                                            });
                                            
                                            // Also call field.onChange to maintain Controller behavior
                                            field.onChange(checked);
                                            
                                            // Call any additional onChange handler
                                            if (rest.onChange) {
                                                // Create a synthetic event object that mimics checkbox behavior
                                                const syntheticEvent = {
                                                    target: { 
                                                        type: 'checkbox',
                                                        name, 
                                                        checked,
                                                        value: checked
                                                    }
                                                };
                                                rest.onChange(syntheticEvent as any);
                                            }
                                        }}
                                        onBlur={() => {
                                            field.onBlur();
                                            trigger(name);
                                            
                                            if (rest.onBlur) {
                                                rest.onBlur({} as any);
                                            }
                                        }}
                                        disabled={rest.disabled}
                                        // Additional props
                                        {...(rest as any)}
                                    />
                                    <span className="ml-2">{rest.placeholder || ' '}</span>
                                </SwitchContainer>
                                {error && (
                                    <FormFeedback>{errorMessage}</FormFeedback>
                                )}
                            </>
                        );
                    } else {
                        // Regular input field
                        return (
                            <>
                                <StrapInput
                                    type={type}
                                    {...commonProps}
                                    {...rest}
                                />
                                {error && (
                                    <FormFeedback>{errorMessage}</FormFeedback>
                                )}
                            </>
                        );
                    }
                }}
            />
        </FormGroup>
    );
};

export default Input;